import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { isLocale, type Locale, type TranslationRecord, SUPPORTED_LOCALES } from './types';
import { en } from './locales/en';
import { bg } from './locales/bg';

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  dictionary: TranslationRecord;
};

const STORAGE_KEY = 'everbranch-locale';
const LOCALE_QUERY_KEY = 'lang';
const DEFAULT_LOCALE = SUPPORTED_LOCALES[0];

const readLocaleFromUrl = (): Locale | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const candidate = params.get(LOCALE_QUERY_KEY);
  return isLocale(candidate) ? candidate : null;
};

const updateLocaleQueryParam = (locale: Locale) => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);

  if (locale === DEFAULT_LOCALE) {
    url.searchParams.delete(LOCALE_QUERY_KEY);
  } else {
    url.searchParams.set(LOCALE_QUERY_KEY, locale);
  }

  if (url.href !== window.location.href) {
    window.history.replaceState(window.history.state, '', url);
  }
};

const readLocaleFromStorage = (): Locale | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isLocale(stored) ? stored : null;
};
export const DICTIONARIES: Record<Locale, TranslationRecord> = {
  en,
  bg,
};

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const urlLocale = readLocaleFromUrl();
    if (urlLocale) {
      return urlLocale;
    }

    const storedLocale = readLocaleFromStorage();
    if (storedLocale) {
      return storedLocale;
    }

    return DEFAULT_LOCALE;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, locale);
    }

    updateLocaleQueryParam(locale);
  }, [locale]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocaleWithUrl = () => {
      const urlLocale = readLocaleFromUrl();
      if (urlLocale && urlLocale !== locale) {
        setLocaleState(urlLocale);
        return;
      }

      if (!urlLocale && locale !== DEFAULT_LOCALE) {
        setLocaleState(DEFAULT_LOCALE);
      }
    };

    window.addEventListener('popstate', syncLocaleWithUrl);

    return () => {
      window.removeEventListener('popstate', syncLocaleWithUrl);
    };
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      dictionary: DICTIONARIES[locale],
    }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocaleContext() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocaleContext must be used within a LocaleProvider');
  }
  return context;
}
